<template>
  <v-dialog
    :value="value"
    max-width="400"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title>
        {{ $t('menu.avatar.title') }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-5 text-center">
        <v-avatar
          size="120"
          color="primary"
          class="mb-4"
        >
          <v-img
            v-if="preview"
            :src="preview"
          />
          <span
            v-else
            class="white--text text-h4"
          >
            {{ initials }}
          </span>
        </v-avatar>
        <div>
          <Button
            variant="secondary"
            small
            @click="openFilePicker"
          >
            {{ $t('menu.avatar.choosePhoto') }}
          </Button>
          <v-btn
            v-if="preview"
            icon
            small
            class="ml-2"
            @click="removeFile"
          >
            <PhTrash :size="18" />
          </v-btn>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".jpeg,.jpg,.png"
          class="d-none"
          @change="handleFileSelect"
        />
        <div
          v-if="fileName"
          class="mt-2 text-caption"
        >
          {{ fileName }}
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <Button
          variant="primary"
          :disabled="!selectedFile"
          :loading="uploading"
          @click="save"
        >
          {{ $t('menu.avatar.save') }}
        </Button>
        <Button
          variant="tertiary"
          @click="close"
        >
          {{ $t('menu.avatar.cancel') }}
        </Button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Api
import UserAvatarApi from '@/api/user/user-avatar-api';

// Components
import Button from '@/components/comum/Button.vue';

// Mixins
import mixinMessage from '@/mixin/mixin-message';

export default {
  name: 'AvatarDialog',
  components: { Button },
  mixins: [mixinMessage],
  inject: ['appData'],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [Number, String],
      default: null
    },
    initials: {
      type: String,
      default: ''
    },
    currentAvatarUrl: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      userAvatarApi: new UserAvatarApi(this.appData.currentUser),
      selectedFile: null,
      preview: null,
      fileName: '',
      uploading: false
    };
  },
  created() {
    this.preview = this.currentAvatarUrl;
  },
  methods: {
    openFilePicker() {
      this.$refs.fileInput?.click();
    },

    handleFileSelect(event) {
      const file = event.target.files?.[0];

      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/png'];

      if (!allowedTypes.includes(file.type)) {
        this.sendMessage(this.$t('menu.avatar.formatError'), 'error');

        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.sendMessage(this.$t('menu.avatar.sizeError'), 'error');

        return;
      }

      this.selectedFile = file;
      this.fileName = file.name;
      this.preview = URL.createObjectURL(file);
    },

    removeFile() {
      this.selectedFile = null;
      this.preview = null;
      this.fileName = '';

      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    async save() {
      if (!this.selectedFile) return;

      this.uploading = true;

      try {
        const formData = new FormData();
        formData.append('avatar', this.selectedFile);

        await this.userAvatarApi.upload(this.userId, formData);
        this.sendMessage(this.$t('menu.avatar.updatedSuccess'), 'success');
        this.$emit('avatar-updated');
        this.close();
      } catch (error) {
        this.sendErrorMessage(error, this.$t('menu.avatar.uploadError'));
      } finally {
        this.uploading = false;
      }
    },

    close() {
      this.selectedFile = null;
      this.preview = null;
      this.fileName = '';
      this.$emit('input', false);
    }
  }
};
</script>
